import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import RegisterModal from '../../components/Auth/RegisterModal';
import LoginModal from '../../components/Auth/LoginModal';
import AiAskBox from '../../components/AiAskBox/AiAskBox';
import FeaturePromo from '../../components/FeaturePromo/FeaturePromo';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import usePickerLanguage from '../ReadingTypes/usePickerLanguage';
import { ensureTarotScripts, tarotScriptsReady } from '../ReadingTypes/loadTarotScripts';
import '../FreeTarot/FreeTarot.css';

const YesNoTarot = () => {
  const [showRegister, setShowRegister] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  usePickerLanguage('yesNo');

  useEffect(() => {
    if (!document.getElementById('tarot-reader-styles')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = '/tarot-reader/cards-only.css';
      link.id = 'tarot-reader-styles';
      document.head.appendChild(link);
    }

    let cancelled = false;
    const alreadyLoaded = tarotScriptsReady();
    const autoSelectYesNo = () => {
      const waitForApp = setInterval(() => {
        if (cancelled || (window.tarotApp && typeof window.tarotApp.selectSpread === 'function')) {
          clearInterval(waitForApp);
          if (!cancelled && window.tarotApp) window.tarotApp.selectSpread('yesNo');
        }
      }, 100);
      setTimeout(() => clearInterval(waitForApp), 5000);
    };

    ensureTarotScripts()
      .then(() => {
        if (cancelled) return;
        if (alreadyLoaded) window.initTarotApp();
        autoSelectYesNo();
      })
      .catch((error) => {
        console.error('Failed to load tarot scripts:', error);
      });

    return () => {
      cancelled = true;
      if (window.tarotApp) {
        window.tarotApp = null;
      }
      if (typeof window.cleanupTarotDeck === 'function') {
        window.cleanupTarotDeck();
      }
    };
  }, []);

  const yesNoJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How does a yes or no tarot reading work?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "You focus on a specific yes-or-no question and draw a single card from the 78-card Tarot deck. Each card carries a traditional yes, no, or maybe energy. The card you draw reveals the Universe's answer to your question, along with additional insight into the situation."
        }
      },
      {
        "@type": "Question",
        "name": "How accurate is a yes or no tarot reading?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Tarot readings reflect the energies and possibilities surrounding your question at this moment. While they provide powerful guidance, remember that your choices and actions shape your future. The cards reveal the most likely outcome based on your current path."
        }
      },
      {
        "@type": "Question",
        "name": "Can I ask the same question twice?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "It's best to ask a question only once per session. Repeatedly drawing cards for the same question can muddy the reading. If you received a 'maybe,' consider rephrasing your question or waiting for the situation to develop before asking again."
        }
      },
      {
        "@type": "Question",
        "name": "What does 'maybe' mean in a tarot reading?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "A 'maybe' answer typically means the outcome depends on decisions you haven't yet made, or that the situation is still evolving. Cards like The Hermit, Death, and the Seven of Cups often indicate a need for patience and reflection before the answer becomes clear."
        }
      },
      {
        "@type": "Question",
        "name": "Is this tarot reading free?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! This yes or no tarot reading is 100% free with no signup or credit card required. Draw as many cards as you like. For a deeper, personalized reading, ask our AI tarot reader in chat."
        }
      }
    ]
  };

  return (
    <div className="free-tarot-page">
      <Helmet>
        <title>Free Yes or No Tarot Reading Online | NowTarot AI</title>
        <meta name="description" content="Get a free yes or no tarot reading online. Draw one card for a clear yes, no, or maybe answer to your question. 100% free, no signup required." />
        <meta name="keywords" content="yes or no tarot, yes no tarot reading, free yes or no tarot, tarot yes no, one card tarot reading" />
        <link rel="canonical" href="https://nowtarot.com/yes-or-no-tarot/" />
        <meta property="og:title" content="Free Yes or No Tarot Reading Online | NowTarot AI" />
        <meta property="og:description" content="Draw one card for a clear yes, no, or maybe answer. 100% free, no signup required." />
        <meta property="og:image" content="https://nowtarot.com/og-yes-or-no.png" />
        <meta property="og:url" content="https://nowtarot.com/yes-or-no-tarot/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Free Yes or No Tarot Reading Online" />
        <meta name="twitter:description" content="Draw one card for a clear yes, no, or maybe answer. 100% free." />
        <meta name="twitter:image" content="https://nowtarot.com/og-yes-or-no.png" />
        <script type="application/ld+json">{JSON.stringify(yesNoJsonLd)}</script>
      </Helmet>
      <Header />
      <div className="stars"></div>
      <div className="container">
        <header className="tarot-header">
          <h1 className="page-title">Yes or No Tarot Reading</h1>
        </header>

        <section className="intro-section">
          <p>
            Get a clear <strong>yes or no answer</strong> from the Tarot cards. Focus on your question,
            draw a single card, and receive instant guidance from the Universe. 100% free, no signup required.
          </p>
          <h2>How It Works</h2>
          <p>
            Think of a yes or no question. Click "Shuffle" to shuffle the virtual deck, then pick one card.
            Each of the 78 Tarot cards carries a <strong>yes</strong>, <strong>no</strong>, or <strong>maybe</strong> energy
            based on centuries of Tarot tradition. Your card will be interpreted with a clear answer and deeper meaning.
          </p>
        </section>

        {/* Main Content */}
        <main className="main-content">
          {/* Deck Area */}
          <section className="deck-section visible" id="deckSection">
 
            {/* Hidden: spread defaults to Celtic Cross Classic */}
            <div className="spread-select-wrap" style={{ display: 'none' }}>
              <label htmlFor="spreadSelect">Choose your spread:</label>
              <select id="spreadSelect" className="spread-select">
                {/* Options will be generated by JS */}
              </select>
            </div>
            
            <p className="cards-needed" id="cardsNeeded">Please choose 1 Tarot card for your reading!</p>
            
            {/* Hidden: reversed cards disabled by default */}
            <div className="options-inline" style={{ display: 'none' }}>
              <label className="toggle-label">
                <input type="checkbox" id="enableReversed" className="toggle-checkbox" />
                <span className="toggle-switch"></span>
                <span>Enable Reversed Cards</span>
              </label>
              <div className="slider-container" id="sliderContainer">
                <label htmlFor="reversedChance">Chance: <span id="chanceValue">25</span>%</label>
                <input type="range" id="reversedChance" min="5" max="50" defaultValue="25" className="slider" />
              </div>
            </div>
            
            <div id="cardarea-wrap" className="cardarea-wrap">
              <div id="cardarea">
                {/* Card deck will be generated by JS */}
              </div>
            </div>
            
            <p className="deck-instruction" id="deckInstruction"><em>Focus on your question, then click a card for your answer!</em></p>
            
            <div className="deck-controls deck-controls-single">
              <button type="button" className="btn btn-reset" id="shuffle-again">
                Shuffle Again
              </button>
            </div>
            
            <button className="btn btn-interpret" id="interpretBtn" disabled style={{display: 'none'}}>
              ✨ Reveal Your Answer ✨
            </button>
          </section>

          {/* Interpretation Display */}
          <section className="interpretation-section" id="interpretationSection">
            <h3>Your Answer</h3>
            <div className="interpretation-content" id="interpretationContent">
              {/* Interpretations will be displayed here */}
            </div>
            <button className="btn btn-new-reading" id="newReadingBtn">
              Ask Another Question
            </button>
          </section>

          <section className="cta-section ft-ask-band">
            <h3>Ask the AI tarot</h3>
            <p>Type a question. Get cards and an answer — no signup.</p>
            <AiAskBox onSignUp={() => setShowRegister(true)} />
          </section>

          <FeaturePromo variant="page" seed="/yes-or-no-tarot/" exclude="/yes-or-no-tarot/" />

          {/* SEO Content: Tips */}
          <section className="about-section">
            <h3>Tips for Your Yes or No Tarot Reading</h3>
            <div className="readings-info">
              <div className="reading-type">
                <h4>Ask Clear Questions</h4>
                <p>The clearer your question, the clearer your answer. Instead of "What about my love life?" try "Will I meet someone new this month?" Frame your question so the answer can be yes, no, or maybe.</p>
              </div>
              <div className="reading-type">
                <h4>Focus Your Energy</h4>
                <p>Before drawing your card, take a moment to breathe and focus on your question. The Tarot responds to your energy and intention. A calm, focused mind leads to more meaningful readings.</p>
              </div>
              <div className="reading-type">
                <h4>Trust the Cards</h4>
                <p>If you receive a "maybe," it often means the situation is still unfolding or depends on choices you haven't yet made. Don't redraw seeking a different answer — trust what the Universe reveals.</p>
              </div>
            </div>
          </section>

          {/* SEO Content: Common Questions */}
          <section className="about-section">
            <h3>Popular Yes or No Tarot Questions</h3>
            <div className="readings-info">
              <div className="reading-type">
                <h4>Love & Relationships</h4>
                <p>"Does he/she have feelings for me?" • "Will we get back together?" • "Is this person my soulmate?" • "Should I tell them how I feel?" • "Will I find love this year?"</p>
              </div>
              <div className="reading-type">
                <h4>Career & Money</h4>
                <p>"Will I get the job?" • "Should I accept this offer?" • "Is now a good time to invest?" • "Will my business succeed?" • "Should I ask for a raise?"</p>
              </div>
              <div className="reading-type">
                <h4>Life Decisions</h4>
                <p>"Should I move to a new city?" • "Is this the right time to make a change?" • "Will things get better?" • "Am I on the right path?" • "Should I trust this person?"</p>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="faq-section">
            <h3>Yes or No Tarot FAQ</h3>
            <div className="faq-list">
              <div className="faq-item">
                <h4>How does a yes or no tarot reading work?</h4>
                <p>You focus on a specific yes-or-no question and draw a single card from the 78-card Tarot deck. Each card carries a traditional yes, no, or maybe energy. The card you draw reveals the Universe's answer to your question, along with additional insight into the situation.</p>
              </div>
              <div className="faq-item">
                <h4>How accurate is a yes or no tarot reading?</h4>
                <p>Tarot readings reflect the energies and possibilities surrounding your question at this moment. While they provide powerful guidance, remember that your choices and actions shape your future. The cards reveal the most likely outcome based on your current path.</p>
              </div>
              <div className="faq-item">
                <h4>Can I ask the same question twice?</h4>
                <p>It's best to ask a question only once per session. Repeatedly drawing cards for the same question can muddy the reading. If you received a "maybe," consider rephrasing your question or waiting for the situation to develop before asking again.</p>
              </div>
              <div className="faq-item">
                <h4>What does "maybe" mean in a tarot reading?</h4>
                <p>A "maybe" answer typically means the outcome depends on decisions you haven't yet made, or that the situation is still evolving. Cards like The Hermit, Death, and the Seven of Cups often indicate a need for patience and reflection before the answer becomes clear.</p>
              </div>
              <div className="faq-item">
                <h4>Is this tarot reading free?</h4>
                <p>Yes! This yes or no tarot reading is 100% free with no signup or credit card required. Draw as many cards as you like. For a deeper, personalized reading, ask our AI tarot reader in chat.</p>
              </div>
            </div>
          </section>

          {/* More Readings Link */}
          <section className="about-section" style={{ textAlign: 'center' }}>
            <p>Looking for more depth? Try a <a href="/three-card-tarot/" style={{ color: '#FFD700' }}>3-card past, present, future</a> reading, a <a href="/love-tarot-reading/" style={{ color: '#FFD700' }}>love tarot reading</a>, or the full <a href="/celtic-cross-tarot/" style={{ color: '#FFD700' }}>10-card Celtic Cross</a> — free, instant, and no signup required. Curious about a specific card? Browse <a href="/tarot-card-meanings/" style={{ color: '#FFD700' }}>tarot card meanings</a>.</p>
          </section>
        </main>
      </div>
      <Footer />
      {showRegister && (
        <RegisterModal
          onClose={() => setShowRegister(false)}
          onSwitchToLogin={() => {
            setShowRegister(false);
            setShowLogin(true);
          }}
        />
      )}

      {showLogin && (
        <LoginModal
          onClose={() => setShowLogin(false)}
          onSwitchToRegister={() => {
            setShowLogin(false);
            setShowRegister(true);
          }}
        />
      )}
    </div>
  );
};

export default YesNoTarot;
